import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Flag, Trash2, Ban, UserX, Users, FileText,
  Clock, Search, ExternalLink, AlertTriangle,
  ShieldCheck, Eye, ChevronRight, Filter, RefreshCw,
  TrendingUp, MessageSquare, AlertOctagon
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [reportedPosts, setReportedPosts] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [reportSearch, setReportSearch] = useState('');
  const [reportDateRange, setReportDateRange] = useState('all');
  const [bannedSearch, setBannedSearch] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logDateRange, setLogDateRange] = useState('all');

  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [postsRes, usersRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/reported-posts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/banned-users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/logs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setReportedPosts(postsRes.data.posts || []);
      setBannedUsers(usersRes.data.users || []);
      setAdminLogs(logsRes.data.logs || []);
    } catch { toast.error('Failed to load admin data'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const applyDateFilter = (items, field, range) => {
    if (range === 'all') return items;
    const cutoff = new Date(Date.now() - parseInt(range) * 86400000);
    return items.filter(i => new Date(i[field]) >= cutoff);
  };

  const filteredReports = useMemo(() => {
    let list = applyDateFilter(reportedPosts, 'created_at', reportDateRange);
    if (reportSearch.trim()) {
      const q = reportSearch.toLowerCase();
      list = list.filter(p => p.author_username?.toLowerCase().includes(q) || p.content?.toLowerCase().includes(q));
    }
    return list;
  }, [reportedPosts, reportSearch, reportDateRange]);

  const filteredBanned = useMemo(() => {
    if (!bannedSearch.trim()) return bannedUsers;
    const q = bannedSearch.toLowerCase();
    return bannedUsers.filter(u => u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  }, [bannedUsers, bannedSearch]);

  const filteredLogs = useMemo(() => {
    let list = applyDateFilter(adminLogs, 'created_at', logDateRange);
    if (logSearch.trim()) {
      const q = logSearch.toLowerCase();
      list = list.filter(l => l.action?.toLowerCase().includes(q) || l.admin_email?.toLowerCase().includes(q) || l.reason?.toLowerCase().includes(q));
    }
    return list;
  }, [adminLogs, logSearch, logDateRange]);

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, postId, userId } = confirmAction;
    const reason = actionReason.trim() || 'Admin moderation';
    try {
      await axios.post(
        `${API}/admin/action`,
        { action: type, target_id: postId || userId, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Action completed and logged');
      setConfirmAction(null);
      setActionReason('');
      setSelectedPost(null);
      loadData(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const getTimeAgo = (ts) => {
    const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getSeverity = (count) => {
    if (count >= 20) return { label: 'Critical', bg: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700' };
    if (count >= 10) return { label: 'High', bg: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300 border border-orange-200' };
    return { label: 'Medium', bg: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 border border-yellow-200' };
  };

  const getActionColor = (action) => {
    if (!action) return 'bg-gray-400';
    if (action.includes('ban') && !action.includes('unban')) return 'bg-red-500';
    if (action.includes('unban')) return 'bg-green-500';
    if (action.includes('delete')) return 'bg-orange-500';
    if (action.includes('shadow')) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const DateFilter = ({ value, onChange }) => (
    <div className="flex items-center gap-2">
      <Filter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <div className="flex rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden">
        {[{ k: '30', l: '30d' }, { k: '180', l: '6mo' }, { k: '365', l: '1yr' }, { k: 'all', l: 'All' }].map(({ k, l }) => (
          <button key={k} onClick={() => onChange(k)}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${value === k ? 'bg-cyan-500 text-white' : 'text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="text-center flex-1">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Admin Dashboard</h1>
              <p className="text-gray-500 dark:text-slate-400 text-sm">Moderate content · Manage users · Review logs</p>
            </div>
            <button onClick={() => loadData(true)} className={`p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition ${refreshing ? 'animate-spin text-cyan-500' : ''}`} title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 text-center">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-2">
                <Flag className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{reportedPosts.length}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Reported Posts</p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 text-center">
              <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-2">
                <UserX className="w-5 h-5 text-orange-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{bannedUsers.length}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Banned / Restricted</p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 text-center">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-2">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{adminLogs.length}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Actions Logged</p>
            </div>
          </div>

          <Tabs defaultValue="reports" className="w-full">
            <TabsList className="grid w-full max-w-sm mx-auto grid-cols-3 mb-8 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-1">
              <TabsTrigger value="reports" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-red-500 data-[state=active]:text-white transition-all">
                <Flag className="w-3.5 h-3.5 mr-1" />Reports {reportedPosts.length > 0 && `(${reportedPosts.length})`}
              </TabsTrigger>
              <TabsTrigger value="banned" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white transition-all">
                <UserX className="w-3.5 h-3.5 mr-1" />Banned
              </TabsTrigger>
              <TabsTrigger value="logs" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white transition-all">
                <FileText className="w-3.5 h-3.5 mr-1" />Logs
              </TabsTrigger>
            </TabsList>

            {/* ── REPORTS ── */}
            <TabsContent value="reports">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input placeholder="Search by username or content…" value={reportSearch} onChange={e => setReportSearch(e.target.value)} className="pl-9 dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm" />
                  </div>
                  <DateFilter value={reportDateRange} onChange={setReportDateRange} />
                </div>

                {filteredReports.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 rounded-2xl bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-3">
                      <ShieldCheck className="w-8 h-8 text-green-500" />
                    </div>
                    <p className="font-medium text-gray-700 dark:text-slate-300">
                      {reportSearch || reportDateRange !== 'all' ? 'No results found' : 'Community is clean! 🎉'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">No reported posts requiring action</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-slate-700">
                    {filteredReports.map(post => {
                      const sev = getSeverity(post.report_count);
                      return (
                        <div key={post.id} className="p-5 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                          {/* Post meta row */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {post.report_count}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Link to={`/profile/${post.author_username}`} className="font-semibold text-gray-900 dark:text-white hover:text-cyan-600 hover:underline flex items-center gap-1 text-sm">
                                    {post.author_username}
                                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                                  </Link>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sev.bg}`}>{sev.label}</span>
                                </div>
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{getTimeAgo(post.created_at)}</p>
                              </div>
                            </div>
                            <button onClick={() => setSelectedPost(post)} className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 flex-shrink-0">
                              <Eye className="w-3.5 h-3.5" /> Details
                            </button>
                          </div>

                          {/* Post content preview */}
                          <p className="text-sm text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 mb-4 line-clamp-2 leading-relaxed">
                            {post.content}
                          </p>

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => setConfirmAction({ type: 'shadow_ban_user', label: 'Shadow Ban User', userId: post.author_id, postId: null })}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition"
                            >
                              <UserX className="w-3.5 h-3.5" /> Shadow Ban
                            </button>
                            <button
                              onClick={() => setConfirmAction({ type: 'ban_user', label: 'Ban User', userId: post.author_id, postId: null })}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                            >
                              <Ban className="w-3.5 h-3.5" /> Ban User
                            </button>
                            <button
                              onClick={() => setConfirmAction({ type: 'delete_post', label: 'Delete Post', postId: post.id, userId: null })}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition ml-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete Post
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ── BANNED ── */}
            <TabsContent value="banned">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-slate-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input placeholder="Search by username or email…" value={bannedSearch} onChange={e => setBannedSearch(e.target.value)} className="pl-9 dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm" />
                  </div>
                </div>

                {filteredBanned.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 rounded-2xl bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-3">
                      <Users className="w-8 h-8 text-green-500" />
                    </div>
                    <p className="font-medium text-gray-700 dark:text-slate-300">
                      {bannedSearch ? 'No results found' : 'No banned users'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-slate-700">
                    {filteredBanned.map(u => (
                      <div key={u.id} className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <img src={u.avatar_url} alt={u.username} className="w-10 h-10 rounded-full flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 dark:text-white text-sm">{u.username}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                u.status === 'banned'
                                  ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                  : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                              }`}>
                                {u.status === 'banned' ? '🚫 Banned' : '👁 Shadow'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Link to={`/profile/${u.username}`}>
                            <button className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition border border-cyan-200 dark:border-cyan-800">
                              <ExternalLink className="w-3 h-3" /> View
                            </button>
                          </Link>
                          <button
                            onClick={() => setConfirmAction({ type: 'unban_user', label: 'Unban User', userId: u.id, postId: null })}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition"
                          >
                            <ShieldCheck className="w-3 h-3" /> Unban
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ── LOGS ── */}
            <TabsContent value="logs">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input placeholder="Search logs…" value={logSearch} onChange={e => setLogSearch(e.target.value)} className="pl-9 dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm" />
                  </div>
                  <DateFilter value={logDateRange} onChange={setLogDateRange} />
                </div>

                {filteredLogs.length === 0 ? (
                  <div className="text-center py-20">
                    <FileText className="w-12 h-12 text-gray-200 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-slate-400">No logs found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-slate-700">
                    {filteredLogs.map(log => (
                      <div key={log.id} className="px-5 py-3.5 flex items-center justify-between gap-4 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getActionColor(log.action)}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {log.action?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                              {log.admin_email}{log.reason ? ` · ${log.reason}` : ''}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-slate-500 flex items-center gap-1 flex-shrink-0">
                          <Clock className="w-3 h-3" />{getTimeAgo(log.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Post Detail Dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertOctagon className="w-5 h-5" /> Report Details
            </DialogTitle>
          </DialogHeader>
          {selectedPost && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Link to={`/profile/${selectedPost.author_username}`} onClick={() => setSelectedPost(null)}>
                  <div className="flex items-center gap-2 group">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                      {selectedPost.author_username?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white group-hover:text-cyan-600 group-hover:underline flex items-center gap-1 text-sm">
                        {selectedPost.author_username} <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-xs text-gray-500">{getTimeAgo(selectedPost.created_at)}</p>
                    </div>
                  </div>
                </Link>
                <span className={`text-sm font-semibold px-3 py-1 rounded-full ${getSeverity(selectedPost.report_count).bg}`}>
                  {selectedPost.report_count} reports
                </span>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-xl text-sm text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
                {selectedPost.content}
              </div>
              <div className="text-xs text-gray-400 dark:text-slate-500 space-y-1 font-mono">
                <p>Post ID: {selectedPost.id}</p>
                <p>Author ID: {selectedPost.author_id}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => { setConfirmAction({ type: 'shadow_ban_user', label: 'Shadow Ban', userId: selectedPost.author_id, postId: null }); setSelectedPost(null); }}
                  className="flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-xl border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition">
                  <UserX className="w-4 h-4" /> Shadow Ban
                </button>
                <button onClick={() => { setConfirmAction({ type: 'ban_user', label: 'Ban User', userId: selectedPost.author_id, postId: null }); setSelectedPost(null); }}
                  className="flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-xl border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                  <Ban className="w-4 h-4" /> Ban User
                </button>
                <button onClick={() => { setConfirmAction({ type: 'delete_post', label: 'Delete Post', postId: selectedPost.id, userId: null }); setSelectedPost(null); }}
                  className="col-span-2 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white transition">
                  <Trash2 className="w-4 h-4" /> Delete Post
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => { setConfirmAction(null); setActionReason(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" /> Confirm: {confirmAction?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-slate-400">
              This action will be permanently logged. You can optionally provide a reason:
            </p>
            <Input
              placeholder="Reason (optional)…"
              value={actionReason}
              onChange={e => setActionReason(e.target.value)}
              className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              onKeyDown={e => e.key === 'Enter' && executeAction()}
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 dark:border-slate-600 dark:text-slate-300" onClick={() => { setConfirmAction(null); setActionReason(''); }}>
                Cancel
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={executeAction}>
                <ChevronRight className="w-4 h-4 mr-1" /> Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;